import React from 'react';
import { Alert, AlertTitle, Box, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BaseBannerProps } from '../types';
import { BANNER_DIV_ID } from 'constants/HTMLAttributes';
import { useThemeContext } from 'contexts/theme';
import { isTenantExpUS } from '@tenants/utils/which-tenant';

interface BaseBannerInternalProps extends BaseBannerProps {
  /** Whether to show close button */
  showCloseButton?: boolean;

  /** Custom close button element */
  closeButton?: React.ReactNode;

  /** Custom action button element */
  actionButton?: React.ReactNode;

  /** Additional content to render */
  children?: React.ReactNode;
}

const BaseBanner: React.FC<BaseBannerInternalProps> = ({
  message,
  title,
  severity = 'info',
  showIcon = true,
  icon,
  iconInlineWithTitle = false,
  className,
  sx,
  onDismiss,
  showCloseButton = false,
  closeButton,
  actionButton,
  children,
  testId,
}) => {
  const { theme, tenantId } = useThemeContext();


  const defaultSx = {
    position: 'relative',
    paddingRight: showCloseButton ? '48px' : undefined,
    padding: '28px 24px',
    backgroundColor: isTenantExpUS(tenantId) ? theme.colors.tertiaryBackground : '#F3F4F4',
    color: isTenantExpUS(tenantId) ? theme.colors.primaryFontColor : '#000000',
    borderRadius: isTenantExpUS(tenantId) ? theme.buttons.borderRadiusBanner : 2,
    boxShadow: isTenantExpUS(tenantId) ? theme.decorations.boxShadow : '0px 4px 12px rgba(0, 0, 0, 0.1)',
    border: 'none',
    '& .MuiAlert-icon': {
      fontSize: '18px',
      marginRight: '16px',
      alignSelf: 'baseline',
      display: 'flex',
      alignItems: 'baseline',
      color: isTenantExpUS(tenantId) ? theme.colors.primaryFontColor : '#000000',
      '& .MuiSvgIcon-root': {
        fontWeight: 'normal',
        color: isTenantExpUS(tenantId) ? theme.colors.primaryFontColor : '#000000',
        fontSize: '18px',
        verticalAlign: 'baseline',
      },
    },
    '& .MuiAlert-message': {
      width: '100%',
      padding: 0,
    },
    fontFamily: isTenantExpUS(tenantId) ? theme.fonts.text : 'Mullish, Arial, sans-serif',
    ...sx,
  };

  // If icon should be inline with title, combine them
  const renderedTitle = iconInlineWithTitle && icon && title && typeof title === 'string' ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
      </span>
      {title}
    </span>
  ) : title;

  return (
    <Alert
      id={BANNER_DIV_ID}
      severity={severity}
      icon={showIcon && !iconInlineWithTitle ? icon : false}
      className={className}
      sx={defaultSx}
      data-testid={testId}
      variant="outlined"
    >
      {showCloseButton && (
        <IconButton
          onClick={onDismiss}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            padding: '4px',
            color: isTenantExpUS(tenantId) ? `${theme.colors.primaryFontColor} !important` : '#000000 !important',
            border: 'none !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            '&:hover': {
              backgroundColor: isTenantExpUS(tenantId) ? `${theme.colors.primaryFocusColor} !important` : 'rgba(0, 0, 0, 0.04) !important',
            },
          }}
          size="small"
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      {renderedTitle && (
        <AlertTitle
          sx={{
            fontWeight: isTenantExpUS(tenantId) ? theme.fonts.headingBoldWeight : 'bold',
            color: isTenantExpUS(tenantId) ? theme.colors.primaryFontColor : '#000000',
            fontFamily: isTenantExpUS(tenantId) ? theme.fonts.text : 'Mullish, Arial, sans-serif',
            marginBottom: 0.5,
          }}
        >
          {renderedTitle}
        </AlertTitle>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 1 },
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontFamily: isTenantExpUS(tenantId) ? theme.fonts.text : 'Mullish, Arial, sans-serif',
              color: isTenantExpUS(tenantId) ? theme.colors.primaryFontColor : '#000000',
              '& strong, & b': {
                fontWeight: isTenantExpUS(tenantId) ? theme.fonts.headingBoldWeight : 'bold',
              },
              '& *:not(strong):not(b)': {
                fontWeight: 'normal',
              },
            }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
          {children}
        </Box>

        {(actionButton || closeButton) && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignSelf: { xs: 'flex-start', sm: 'center' },
            }}
          >
            {actionButton}
            {closeButton}
          </Box>
        )}
      </Box>
    </Alert>
  );
};

export default BaseBanner;